import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Coins,
    Wallet,
    ArrowRight,
    Plus,
    Trash2,
    Save,
    Loader2,
    Calendar,
    Building2,
    MessageSquare,
    DollarSign,
    ReceiptText,
    CheckCircle2
} from 'lucide-react';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import DateInput from '../components/ui/DateInput'; // Adapting to your Date/DateTime layout
import { useToast } from '../hooks/useToast';
import {
    fetchCurrencies,
    fetchAllBankAccounts,
    fetchTransactionTypes,
    fetchStoresWhereStaffAssigned,
    generateUniquePoNumber
} from '../apiHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import FastAPIClient from '../services/apiClient';

const client = new FastAPIClient();

const ExpenseLogging = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Mock user context - replace with your application's auth hook/state
    const [user, setUser] = useState(null);

    // Workflow Stages: 'funding' | 'disbursing'
    const [stage, setStage] = useState(() => {
        return localStorage.getItem('expense_stage') || 'funding'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Master Dropdown Data Options
    const [currencyOptions, setCurrencyOptions] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);

    // Stage 1 State: Initial Petty Cash Allocation Intent
    const [fundingAmount, setFundingAmount] = useState(() => {
        return localStorage.getItem('expense_funding_amount') || '';
    });
    const [displayAmount, setDisplayAmount] = useState(() => {
        return localStorage.getItem('expense_display_amount') || '';
    });
    const [fundingCurrency, setFundingCurrency] = useState('NGN'); // Default currency stub

    // Stage 2 State: Multi-line expense logs
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [expenseDate, setExpenseDate] = useState(() => {
        const now = new Date();
        const offset = now.getTimezoneOffset() * 60000;
        return new Date(now-offset).toISOString().slice(0,16);
    });
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [expenseLines, setExpenseLines] = useState([
        { 
            // id: crypto.randomUUID(), 
            id: generateUniquePoNumber(),
            transactionType: '', 
            fundingAccount: '', 
            amount: '', 
            comment: '' 
        }
    ]);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to use the Point of Sale.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // ----------------------------------------------------
    // 1. Data Fetching & Options Formatting
    // ----------------------------------------------------
    useEffect(() => {
        const loadInitialMetadata = async () => {

            if (!user) return;

            try {
                // Fetch and parse Currencies
                const fetchedCurrencies = await fetchCurrencies();
                const currencies = fetchedCurrencies.map(currency => ({
                    label: `${currency.setccurr} - ${currency.setcnote}`,
                    value: currency.setccurr,
                }));
                setCurrencyOptions(currencies);
                if (currencies.length > 0) {
                    setSelectedCurrency(currencies[0].value);
                    setFundingCurrency(currencies[0].value);
                }

                // Fetch and parse Bank Leaf Accounts
                const accounts = await fetchAllBankAccounts();
                const filteredAndSortedAccounts = accounts
                    .filter(a => a.is_active && a.is_leaf)
                    .map(a => ({
                        label: `${a.name} (${a.account_code})`,
                        value: a.account_id,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                setBankAccountOptions(filteredAndSortedAccounts);

                // Fetch, filter and parse Transaction Types containing "expense"
                const fetchedTransactionTypes = await fetchTransactionTypes();
                const filteredTypes = fetchedTransactionTypes
                    .filter(type => type.label.toLowerCase().includes('expense'))
                    .filter(type => !type.label.toLowerCase().includes('void'))
                    .map(type => ({
                        label: type.label,
                        value: type.transaction_type,
                    }));
                setTransactionTypes(filteredTypes);

                // Fetch and parse assigned branches/stores
                const fetchedStores = await fetchStoresWhereStaffAssigned(user);
                const stores = fetchedStores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                    address: store.address
                }));
                setStoreOptions(stores);
                if (stores.length > 0) {
                    setSelectedStoreId(stores[0].value);
                }

            } catch (error) {
                console.error("Error setting up expense context metadata:", error);
                showToast(error.response?.data?.detail || "Failed to populate setup configuration metrics.", "error");
            }
        };

        loadInitialMetadata();
    }, [user, showToast]);

    // Determine default Petty Cash account match dynamically from loaded accounts
    const pettyCashAccountId = useMemo(() => {
        const found = bankAccountOptions.find(acc => acc.label.toLowerCase().includes('petty cash'));
        return found ? found.value : (bankAccountOptions[0]?.value || '');
    }, [bankAccountOptions]);


    // Determine default Miscellaneous Expense transaction type match dynamically
    const miscExpenseTypeId = useMemo(() => {
        const found = transactionTypes.find(type => 
            type.label.toLowerCase().includes('other expense payment')
        );
        return found ? found.value : (transactionTypes[0]?.value || '');
    }, [transactionTypes]);


    // Apply default pre-selections immediately once Stage 1 resolves or options change
    useEffect(() => {
        if (stage === 'disbursing') {
            setExpenseLines(prev => prev.map(line => ({
                ...line,
                fundingAccount: line.fundingAccount || pettyCashAccountId,
                transactionType: line.transactionType || miscExpenseTypeId
            })));
        }
    }, [stage, pettyCashAccountId, miscExpenseTypeId]);

    // ----------------------------------------------------
    // 2. Action Handlers
    // ----------------------------------------------------

    // Stage 1 Submission Handler
    const handleInitialFundingSubmit = async (e) => {
        e.preventDefault();

        // validate store selection
        if (!selectedStoreId) {
            showToast("Target branch selection is mandatory before initial allocation.", "error");
            return;
        }

        const parsedAmount = parseFloat(fundingAmount);
        if (!parsedAmount || parsedAmount <= 0) {
            showToast("Please enter a valid allocation amount greater than 0.", "warning");
            return;
        }

        setIsSubmitting(true);

        // Simulating payload assembly & sending to backend
        const fundingPayload = {
            intent: "FUND_PETTY_CASH",
            branch_id: selectedStoreId,
            amount: parsedAmount,
            currency: fundingCurrency,
            timestamp: expenseDate ? new Date(expenseDate).toISOString() : new Date().toISOString(),
            requestedBy: user?.users_id,
        };

        try {
            // console.log("Stage 1 - Payload Sent to Backend:", fundingPayload);
            const response = await client.postData("/accounting/disburse-to-petty-cash", fundingPayload);
            showToast(response.msg || "Successfully funded Petty Cash with total expense amount.");
            localStorage.setItem('expense_stage', 'disbursing');
            localStorage.setItem('expense_funding_amount', fundingAmount);
            localStorage.setItem('expense_display_amount', displayAmount)
            setTimeout(() => {
                setStage('disbursing')
            }, 800);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to submit withdrawal to petty cash.", "error");
        } finally {
            setIsSubmitting(false);
        }

        // setTimeout(() => {
        //     setIsSubmitting(false);
        //     showToast(`Successfully registered intent to fund Petty Cash with ${fundingCurrency} ${parsedAmount.toLocaleString()}`, "success");
        //     setStage('disbursing');
        // }, 800);
    };

    // Stage 2 Line Mutators
    const addExpenseLine = () => {
        // Block additional allocations if the threshold limit has already been completely utilized
        if (lineItemsTotalAmount >= parseFloat(fundingAmount || 0)) {
            showToast("Cannot add line item. The initial funding allocation amount has already been fully utilized.", "error");
            return;
        }

        setExpenseLines(prev => [
            ...prev,
            {
                // id: crypto.randomUUID(),
                id: generateUniquePoNumber(),
                transactionType: miscExpenseTypeId,
                fundingAccount: pettyCashAccountId,
                amount: '',
                comment: ''
            }
        ]);
    };

    const removeExpenseLine = (id) => {
        if (expenseLines.length === 1) {
            showToast("An expense submission must contain at least one detail line item.", "warning");
            return;
        }
        setExpenseLines(prev => prev.filter(line => line.id !== id));
    };

    const updateLineItem = (id, key, value) => {
        setExpenseLines(prev => prev.map(line => {
            if (line.id === id) {
                return { ...line, [key]: value };
            }
            return line;
        }));
    };

    // Compute cumulative running total of lines
    const lineItemsTotalAmount = useMemo(() => {
        return expenseLines.reduce((acc, current) => {
            const val = parseFloat(current.amount) || 0;
            return acc + val;
        }, 0);
    }, [expenseLines]);

    // Stage 2 Main Submission Handler
    const handleDisbursementSubmit = async (e) => {
        e.preventDefault();

        // 1. Core Header Level Valdations
        if (!selectedStoreId) return showToast("Target branch location selection is mandatory.", "error");
        if (!expenseDate) return showToast("Transaction operational logging date is mandatory.", "error");
        if (!selectedCurrency) return showToast("Transaction baseline currency is mandatory.", "error");

        if (lineItemsTotalAmount > parseFloat(fundingAmount || 0)) {
            return showToast(
                `Over-allocation error! Cumulative breakdown rows (${activeCurrencySymbol} ${lineItemsTotalAmount.toLocaleString()}) exceed your initial allocation limit of ${fundingCurrency} ${parseFloat(fundingAmount).toLocaleString()}.`,
                "error"
            );
        }

        // 2. Row Level Field Strict Validations
        for (let i = 0; i < expenseLines.length; i++) {
            const line = expenseLines[i];
            const rowLabel = `Row #${i + 1}`;

            if (!line.transactionType) return showToast(`${rowLabel}: Transaction ledger type specification is mandatory.`, "error");
            if (!line.fundingAccount) return showToast(`${rowLabel}: Disbursing source bank account is mandatory.`, "error");
            if (!line.amount || parseFloat(line.amount) <= 0) return showToast(`${rowLabel}: A numerical layout amount greater than 0 is required.`, "error");
            if (!line.comment || line.comment.trim().length < 3) return showToast(`${rowLabel}: A brief informative comment is compulsory.`, "error");
        }

        setIsSubmitting(true);

        const journalEntryPayload = {
            branch_id: selectedStoreId,
            expense_datetime: expenseDate,
            currency: selectedCurrency,
            stage1_allotment_reference: parseFloat(fundingAmount),
            total_disbursed: lineItemsTotalAmount,
            lines: expenseLines.map(line => ({
                transaction_type: line.transactionType,
                funding_account_id: line.fundingAccount,
                amount: parseFloat(line.amount),
                comment: line.comment.trim()
            }))
        };

        try {
            // console.log("Stage 2 - Comprehensive Expense Disbursement Payload Submitted:", journalEntryPayload);
            const response = await client.postData("/accounting/post-corporate-expenses", journalEntryPayload);
            showToast(response.msg || "Successfully posted expenses", "success");

            // --- CLEAR LocalStorage PARAMS ---
            localStorage.removeItem('expense_stage');
            localStorage.removeItem('expense_funding_amount');
            localStorage.removeItem('expense_display_amount');

            // Reset workflow cycle back to initial stage
            setFundingAmount('');
            setDisplayAmount('');
            setExpenseLines([{ id: generateUniquePoNumber(), transactionType: miscExpenseTypeId, fundingAccount: pettyCashAccountId, amount: '', comment: '' }]);
            setStage('funding');
        } catch (err) {
            showToast(err.response?.data?.detail || "Error posting expenses", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Selected metadata labels lookups for Sidebar Summary Panels
    const activeStoreLabel = storeOptions.find(o => o.value === selectedStoreId)?.label || 'Not Specified';
    const activeCurrencySymbol = selectedCurrency || '---';

    const handleFundingAmountChange = (e) => {
        let inputVal = e.target.value;
        let cleanVal = inputVal.replace(/,/g, '');
        cleanVal = cleanVal.replace(/[^0-9.]/g, '');

        const parts = cleanVal.split('.');
        if (parts.length > 2) return;

        setFundingAmount(cleanVal);

        if (parts[0]) {
            parts[0] = Number(parts[0]).toLocaleString('en-US');
        }

        const formattedVal = parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
        setDisplayAmount(formattedVal || '');
    }

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 text-slate-800">

            {/* Header Identity banner */}
            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-zinc-200 pb-5 gap-4">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-slate-900">Corporate Operational Expenses</h1>
                    <p className="text-sm text-slate-500 mt-1">Fund corporate operational control registries and log micro-disbursements instantly.</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold tracking-wider uppercase bg-zinc-100 border border-zinc-200 p-2 rounded-lg text-slate-600 self-start">
                    <span className={`h-2 w-2 rounded-full ${stage === 'funding' ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
                    Current Stage: {stage === 'funding' ? '1. Fund Allocation Intent' : '2. Expense Disbursement'}
                </div>
            </div>

            {/* ==========================================
                STAGE 1 LAYOUT: BOLD INITIAL INTENT FORM
                ========================================== */}
            {stage === 'funding' && (
                <div className="max-w-2xl mx-auto my-12 animate-fadeIn">
                    <Card className="p-8 border-t-4 border-t-indigo-600 shadow-xl bg-white">
                        <form onSubmit={handleInitialFundingSubmit} className="space-y-8 text-center">
                            <div className="mx-auto bg-indigo-50 text-indigo-600 rounded-full h-16 w-16 flex items-center justify-center shadow-inner">
                                <Coins size={32} />
                            </div>

                            <div className="space-y-2">
                                <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                                    How much do you intend to expend?
                                </h2>
                                <p className="text-sm text-slate-500 max-w-md mx-auto">
                                    ⚠️ Ensure that you have withdrawn the amount below from the physical cash at your branch.
                                </p>
                            </div>

                            <div className="max-w-md mx-auto text-left space-y-1.5">
                                <label className="block text-xs font-bold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                                    <Building2 size={14} className="text-zinc-400" /> Operational Branch
                                </label>
                                <SearchableSelectBox
                                    options={storeOptions}
                                    value={selectedStoreId}
                                    // onChange={setSelectedStoreId}
                                    onChange={(e) => setSelectedStoreId(e.target.value)}
                                    placeholder="Select assigned branch"
                                    required
                                />
                            </div>

                            <div className="max-w-md mx-auto text-left space-y-1.5">
                                <label className="block text-xs font-bold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                                    <Calendar size={14} className="text-zinc-400" /> Operational Date & Time
                                </label>
                                <Input 
                                    type="datetime-local"
                                    required
                                    value={expenseDate}
                                    onChange={(e) => setExpenseDate(e.target.value)}
                                />
                            </div>

                            <div className="max-w-md mx-auto flex items-center gap-3 bg-zinc-50 border-2 border-zinc-200 focus-within:border-indigo-600 rounded-xl p-3 transition-all duration-200 shadow-sm">
                                <div className="w-1/3">
                                    <select
                                        value={fundingCurrency}
                                        onChange={(e) => setFundingCurrency(e.target.value)}
                                        className="w-full bg-transparent font-bold text-slate-700 outline-none cursor-pointer border-r border-zinc-200 pr-2"
                                    >
                                        {currencyOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.value}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="w-2/3 flex items-center">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        value={displayAmount}
                                        onChange={handleFundingAmountChange}
                                        className="w-full bg-transparent text-3xl font-black text-slate-900 placeholder-zinc-300 outline-none text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        required
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-full md:w-auto px-8 py-3.5 text-base font-bold shadow-md shadow-indigo-100 flex items-center justify-center gap-2 mx-auto rounded-xl"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <Loader2 className="animate-spin h-5 w-5" />
                                    ) : (
                                        <>
                                            Initialize Disbursement Route <ArrowRight size={18} />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}

            {/* ==========================================
                STAGE 2 LAYOUT: DETAIL DISBURSEMENT FORM
                ========================================== */}
            {stage === 'disbursing' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start animate-fadeIn">

                    {/* Left & Center Main Column Inputs */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="p-6 bg-white shadow-sm border border-zinc-200">
                            <form onSubmit={handleDisbursementSubmit} className="space-y-6">

                                {/* Header Section Parameters Setup */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-6 border-b border-dashed border-zinc-200">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
                                            <Building2 size={14} className="text-zinc-400" /> Operational Branch
                                        </label>
                                        <SearchableSelectBox
                                            options={storeOptions}
                                            value={selectedStoreId}
                                            // onChange={setSelectedStoreId}
                                            onChange={(e) => setSelectedStoreId(e.target.value)}
                                            placeholder="Select assigned branch"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
                                            <Calendar size={14} className="text-zinc-400" /> Operational Date & Time
                                        </label>
                                        <Input
                                            type="datetime-local"
                                            required
                                            value={expenseDate}
                                            onChange={(e) => setExpenseDate(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
                                            <Coins size={14} className="text-zinc-400" /> Transaction Currency
                                        </label>
                                        <SearchableSelectBox
                                            options={currencyOptions}
                                            value={selectedCurrency}
                                            // onChange={setSelectedCurrency}
                                            onChange={(e) => setSelectedCurrency(e.target.value)}
                                            placeholder="Select Base Currency"
                                        />
                                    </div>
                                </div>

                                {/* Dynamic Expenses Detail Multi-Line Grid Matrix */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                                            <ReceiptText size={16} className="text-indigo-600" /> Breakdown Allocation Lines
                                        </h3>
                                        <Button
                                            type="button"
                                            onClick={addExpenseLine}
                                            variant="secondary"
                                            disabled={lineItemsTotalAmount >= parseFloat(fundingAmount || 0)}
                                            className="px-3 py-1.5 text-xs font-bold flex items-center gap-1 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 text-slate-700 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Plus size={14} /> Add Line Item
                                        </Button>
                                    </div>

                                    <div className="space-y-4">
                                        {expenseLines.map((line, index) => (
                                            <div
                                                key={line.id}
                                                className="p-4 bg-zinc-50/60 rounded-xl border border-zinc-200/80 relative space-y-4 transition hover:shadow-sm"
                                            >
                                                {/* Header context badge indices with remove modifier layout */}
                                                <div className="flex items-center justify-between border-b border-zinc-200/60 pb-2">
                                                    <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">
                                                        Line Item #{index + 1}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeExpenseLine(line.id)}
                                                        className="text-zinc-400 hover:text-red-500 transition-colors duration-150"
                                                        title="Delete row allocation metric"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>

                                                {/* Core row parameters fields inputs schema */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-[11px] font-bold uppercase text-zinc-500 mb-1">
                                                            Funding Target Account
                                                        </label>
                                                        <SearchableSelectBox
                                                            options={bankAccountOptions}
                                                            value={line.fundingAccount}
                                                            // onChange={(val) => updateLineItem(line.id, 'fundingAccount', val)}
                                                            onChange={(e) => updateLineItem(line.id, 'fundingAccount', e.target.value)}
                                                            placeholder="Select Disbursement Account"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-[11px] font-bold uppercase text-zinc-500 mb-1">
                                                            Expense Transaction Category Type
                                                        </label>
                                                        <SearchableSelectBox
                                                            options={transactionTypes}
                                                            value={line.transactionType}
                                                            required
                                                            isDisabled
                                                            // onChange={(val) => updateLineItem(line.id, 'transactionType', val)}
                                                            onChange={(e) => updateLineItem(line.id, 'transactionType', e.target.value)}
                                                            placeholder="Select Type Mapping"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="md:col-span-1">
                                                        <Input
                                                            label="Expended Line Amount *"
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="0.00"
                                                            value={line.amount}
                                                            onChange={(e) => updateLineItem(line.id, 'amount', e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <Input
                                                            label="Compulsory Explanatory Comment / Justification *"
                                                            type="text"
                                                            placeholder="Describe business application purpose (e.g., Office logistics refit components)"
                                                            value={line.comment}
                                                            onChange={(e) => updateLineItem(line.id, 'comment', e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Form Action Controls Area footer integration layout */}
                                <div className="flex items-center justify-between border-t border-zinc-200 pt-6 mt-4">
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            // --- CLEAR LocalStorage PARAMS ---
                                            localStorage.removeItem('expense_stage');
                                            localStorage.removeItem('expense_funding_amount');
                                            localStorage.removeItem('eexpene_display_amount');
                                            setStage('funding');
                                        }}
                                        variant="secondary"
                                        className="text-xs font-bold text-zinc-500 hover:text-zinc-700 bg-transparent border-0 underline shadow-none"
                                    >
                                        Modify Allocation Threshold Intent
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="flex items-center gap-2 px-6 py-2.5 font-bold rounded-lg shadow-sm"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="animate-spin h-4 w-4" />
                                        ) : (
                                            <Save size={16} />
                                        )}
                                        {isSubmitting ? 'Posting Ledger Changes...' : 'Commit Operational Expenses'}
                                    </Button>
                                </div>

                            </form>
                        </Card>
                    </div>

                    {/* Right Hand Sidebar: Continuous Real-Time Aggregate Summary Canvas Panel */}
                    <div className="space-y-6 lg:sticky lg:top-6">

                        {/* Threshold Intent Overview Metrics Card */}
                        <Card className="p-6 border border-zinc-200 bg-white shadow-sm space-y-4">
                            <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
                                <Wallet size={18} className="text-zinc-500" />
                                <h3 className="font-bold text-xs text-zinc-700 uppercase tracking-wider">Fund Target Threshold</h3>
                            </div>
                            <div className="p-4 bg-amber-50/70 border border-amber-100/80 rounded-xl text-center">
                                <p className="text-[10px] font-bold uppercase tracking-tight text-amber-600/90 mb-0.5">Petty Cash Allotment Intent</p>
                                <p className="text-2xl font-black text-amber-700 tracking-tight">
                                    {fundingCurrency} {parseFloat(fundingAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        </Card>

                        {/* Real-time calculated Expense Outflow Accumulation Card */}
                        <Card className="p-6 border border-zinc-200 bg-white shadow-sm space-y-4">
                            <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
                                <CheckCircle2 size={18} className="text-indigo-600" />
                                <h3 className="font-bold text-xs text-zinc-700 uppercase tracking-wider">Outflow Compilation</h3>
                            </div>

                            <div className="space-y-3 text-xs font-medium text-slate-600">
                                <div className="flex justify-between items-center py-1">
                                    <span className="text-zinc-400">Total Rows Declared:</span>
                                    <span className="font-bold text-slate-900">{expenseLines.length} Entries</span>
                                </div>
                                <div className="flex justify-between items-center py-1">
                                    <span className="text-zinc-400">Target Segment Branch:</span>
                                    <span className="font-bold text-slate-900 max-w-[150px] truncate">{activeStoreLabel}</span>
                                </div>
                                <div className="flex justify-between items-center py-1 border-b border-zinc-100 pb-3">
                                    <span className="text-zinc-400">Value Execution Date:</span>
                                    <span className="font-bold text-slate-900">{expenseDate ? new Date(expenseDate).toLocaleDateString() : 'N/A'}</span>
                                </div>

                                <div className="p-4 bg-zinc-900 text-white rounded-xl text-center shadow-inner">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-0.5">Cumulative Items Outflow</p>
                                    <p className="text-3xl font-black text-white tracking-tight">
                                        {activeCurrencySymbol} {lineItemsTotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </p>
                                </div>

                                {/* Smart balance reconciliation status monitor element tracking against initial allotment */}
                                {parseFloat(fundingAmount) > 0 && (
                                    <div className="pt-2 text-[11px] text-center">
                                        {lineItemsTotalAmount > parseFloat(fundingAmount) ? (
                                            <span className="text-red-500 font-bold bg-red-50 px-2 py-1 rounded border border-red-100 block">
                                                Exceeds Initial Funding Intent Limit by {activeCurrencySymbol} {(lineItemsTotalAmount - parseFloat(fundingAmount)).toLocaleString()}
                                            </span>
                                        ) : lineItemsTotalAmount === parseFloat(fundingAmount) ? (
                                            <span className="text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded border border-emerald-100 block">
                                                Perfect Allotment Balancing Attained
                                            </span>
                                        ) : (
                                            <span className="text-zinc-500 font-medium italic block">
                                                {activeCurrencySymbol} {(parseFloat(fundingAmount) - lineItemsTotalAmount).toLocaleString()} remaining within intent envelope buffer.
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>

                </div>
            )}
        </div>
    );
};

export default ExpenseLogging;